import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

export default class extends Controller {
  static targets = ["container"];

  constructor() {
    super();

    this.draggedPostElement = null;
    this.futureDateTime = null;
    this.futureContainerDiv = null;
    this.startDivContainer = null;
    this.overCard = null;
    this.dragOverRAF = null;
  }

  connect() {
    this.boundHandleDialogClose = this.handleDialogClose.bind(this);
    window.addEventListener("dialog:closed", this.boundHandleDialogClose);

    // Create ONE reusable overlay card (perf critical)
    this.overCard = document.createElement("div");
    this.overCard.className =
      "card-over w-full h-[48px] border-2 border-dashed shadow-sm hidden pointer-events-none";
  }

  disconnect() {
    window.removeEventListener("dialog:closed", this.boundHandleDialogClose);
  }

  // ---------------------------
  // MODAL HANDLING
  // ---------------------------
  handleDialogClose() {
    if (!this.draggedPostElement || !this.startDivContainer) return;

    // Restore DOM position safely
    this.startDivContainer.appendChild(this.draggedPostElement);
  }

  // ---------------------------
  // DRAG START
  // ---------------------------
  dragStart(event) {
    if (!event.currentTarget.draggable) return;

    this.draggedPostElement = event.currentTarget;
    this.startDivContainer = event.currentTarget.parentElement;

    // IMPORTANT POUR SAFARI
    event.dataTransfer.setData("text/plain", "drag");
  }

  // ---------------------------
  // DRAG OVER (OPTIMIZED)
  // ---------------------------
  dragOver(event) {
    event.preventDefault();

    const slot = event.currentTarget;

    if (this.dragOverRAF) return;

    this.dragOverRAF = requestAnimationFrame(() => {
      this.dragOverRAF = null;

      // Skip if hovering same slot
      if (slot === this.futureContainerDiv) return;

      // Remove previous highlight
      if (this.futureContainerDiv) {
        this.futureContainerDiv.classList.remove("relative");
      }

      this.futureContainerDiv = slot;

      // Compute future date
      const futureDateTime = slot?.dataset?.datetime;

      if (futureDateTime) {
        this.futureDateTime = new Date(futureDateTime);
      }

      // Move overlay (no DOM recreation)
      slot.classList.add("relative");
      this.overCard.classList.remove("hidden");
      slot.appendChild(this.overCard);
    });
  }

  // ---------------------------
  // DRAG LEAVE / DROP
  // ---------------------------
  dragLeave(event) {
    event.preventDefault();

    if (!this.futureDateTime) return;

    const element = event.currentTarget;

    // Avoid no-op drop
    const alreadyInSlot = Array.from(
      this.futureContainerDiv?.querySelectorAll("button") || []
    ).includes(this.draggedPostElement);

    if (alreadyInSlot) return;

    if (element.nodeName === "BUTTON") {
      const postId = element.dataset.postId;
      const updatePath = `/posts/${postId}`;

      const postOnChannels = JSON.parse(element.dataset.pocs || "[]");

      const body = {
        post: {
          post_on_channels_attributes: postOnChannels.map((poc) => ({
            id: poc.id,
            forced_scheduled_at: this.futureDateTime,
            status: poc.status,
          })),
          scheduled_at: this.futureDateTime,
        },
      };

      this.updatePost(updatePath, body);

      // Move card visually after validation flow
      this.moveCard(element);
    }
  }

  // ---------------------------
  // SERVER UPDATE FLOW
  // ---------------------------
  updatePost(route, data = {}) {
    this.hideOverlay();

    const allScheduled = data?.post?.post_on_channels_attributes?.every(
      (poc) => poc.status === "scheduled"
    );

    if (allScheduled) {
      window.dispatchEvent(
        new CustomEvent("open-dialog", {
          detail: { id: "edit_post_scheduled_at_dialog" },
        })
      );
    }

    const dialogBody = document.querySelector("#form-post-scheduled-at-update");
    if (!dialogBody) return;

    // Clear previous form
    dialogBody.replaceChildren();

    this.buildFormEditPostHTML(
      dialogBody,
      route,
      this.futureDateTime,
      data.post.post_on_channels_attributes,
      data.post.scheduled_at
    );
  }

  // ---------------------------
  // UI HELPERS
  // ---------------------------
  hideOverlay() {
    this.overCard?.classList.add("hidden");
  }

  moveCard(card) {
    if (!this.futureContainerDiv) return;

    // Clear slot except visible items
    Array.from(this.futureContainerDiv.children).forEach((child) => {
      if (child.querySelector("button")?.dataset?.visible) return;
      child.remove();
    });

    const wrapper = document.createElement("div");
    wrapper.classList.add("p-1", "space-y-1", "h-fit", "flex", "flex-col");

    wrapper.appendChild(card);

    this.futureContainerDiv.appendChild(wrapper);
  }

  // ---------------------------
  // FORM BUILDER (UNCHANGED LOGIC)
  // ---------------------------
  buildFormEditPostHTML(div, route, datetime, pocs, scheduled_at) {
    const date = new Date(datetime);
    const form = document.getElementById("form_post_scheduled_at");
    if (!form) return;

    form.action = route;

    // Confirmation message (localized hint + date formatted in the page locale)
    const messageEl = form.querySelector("span");
    if (messageEl) {
      const locale = document.documentElement.lang || "fr";
      const hint = messageEl.dataset.rescheduleHint || messageEl.innerText;
      const formattedDate = new Intl.DateTimeFormat(locale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
      messageEl.innerText = `${hint} ${formattedDate}`;
    }

    // scheduled_at input
    const scheduledInput = document.createElement("input");
    scheduledInput.name = "post[scheduled_at]";
    scheduledInput.value = scheduled_at;
    scheduledInput.classList.add("hidden");
    div.appendChild(scheduledInput);

    // POC inputs
    pocs.forEach((poc, index) => {
      const inputDate = document.createElement("input");
      inputDate.name = `post[post_on_channels_attributes][${index}][forced_scheduled_at]`;
      inputDate.value = poc.forced_scheduled_at;
      inputDate.classList.add("hidden");

      const inputId = document.createElement("input");
      inputId.name = `post[post_on_channels_attributes][${index}][id]`;
      inputId.value = poc.id;
      inputId.classList.add("hidden");

      div.appendChild(inputId);
      div.appendChild(inputDate);
    });
  }
}
